import React, { useRef } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import CustomButton from '../../../components/@extended/CustomButton';
import { VisuallyHiddenInput } from '../../../components/common/DocumentUpload';
import { formatCountryOption, getCountryByCode } from '../../../constants/iso3166Countries';
import {
  useAddFinanceEntityMutation,
  useAssignSubjectToStudySiteMutation,
  useUploadSubjectOnboardingCsvMutation,
} from '../../../store/slices/apiSlice';
import { openMessage } from '../../../store/slices/showMessageSlice';
import { sanitizeApiErrorMessage } from '../../../util/util';
import {
  bankCountries,
  entityOnboardValidationSchema,
  getInitialValues,
  mapFormValuesToPayload,
} from '../../financeModule/studySetup/entityOnboardValidation';
import { PersonFields } from '../../financeModule/studySetup/FinanceEntityOnboardForm';

const subjectInitialValues = () => {
  const base = getInitialValues({
    entityType: 'SUBJECT',
    businessType: 'individual',
  });
  return {
    ...base,
    bankDetails: {
      ...base.bankDetails,
      accountHolderType: 'individual',
    },
  };
};

const OnboardSubjectDialog = ({ open, onClose, protocolNumber, siteEntityId, onSuccess }) => {
  const dispatch = useDispatch();
  const actor = useSelector((state) => state?.userDetails?.loggedInUser?.email);
  const fileInputRef = useRef(null);
  const [addFinanceEntity, { isLoading: isOnboarding }] = useAddFinanceEntityMutation();
  const [assignSubject, { isLoading: isLinking }] = useAssignSubjectToStudySiteMutation();
  const [uploadSubjectCsv, { isLoading: isUploading }] = useUploadSubjectOnboardingCsvMutation();
  const isBusy = isOnboarding || isLinking || isUploading;

  const linkSubjectToSite = async (subjectEmail, { resetForm, setSubmitting, closeOnSuccess = true } = {}) => {
    try {
      const linkResult = await assignSubject({
        protocolNumber,
        siteEntityId,
        subjectEmail,
        actor,
      }).unwrap();

      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: linkResult?.alreadyLinked
            ? 'Subject was already linked to this site.'
            : 'Subject linked to this site successfully.',
        })
      );
      resetForm?.({ values: subjectInitialValues() });
      onSuccess?.();
      if (closeOnSuccess) {
        onClose();
      }
    } catch (error) {
      const messageText = sanitizeApiErrorMessage(error) || '';
      const isStudySiteConflict =
        error?.status === 409 ||
        messageText.toLowerCase().includes('already linked to another site');

      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: isStudySiteConflict
            ? 'This subject is already linked to another site on this clinical study. Link them from that site instead.'
            : messageText || 'Unable to link subject to this site. Please try again.',
        })
      );
    } finally {
      setSubmitting?.(false);
    }
  };

  const handleFileUpload = async (acceptedFiles) => {
    if (!acceptedFiles?.[0] || isBusy) return;
    const formData = new FormData();
    formData.append('file', acceptedFiles[0]);

    try {
      await uploadSubjectCsv({
        protocolNumber,
        siteEntityId,
        body: formData,
        actor,
      }).unwrap();

      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: 'CSV uploaded successfully. Subject onboarding started.',
        })
      );
      onSuccess?.();
    } catch (error) {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: error?.data?.message || 'Failed to upload subject CSV.',
        })
      );
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = mapFormValuesToPayload({ ...values, entityType: 'SUBJECT' });

    try {
      await addFinanceEntity({ body: payload, actor }).unwrap();
    } catch (error) {
      const messageText = sanitizeApiErrorMessage(error) || '';
      if (messageText.toLowerCase().includes('already exists')) {
        dispatch(
          openMessage({
            messageSeverity: 'info',
            message: 'A finance entity with this email already exists. Linking the existing subject to this site.',
          })
        );
        await linkSubjectToSite(payload.email, { resetForm, setSubmitting });
        return;
      }

      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: messageText || 'Unable to onboard subject. Please try again.',
        })
      );
      setSubmitting(false);
      return;
    }

    try {
      const linkResult = await assignSubject({
        protocolNumber,
        siteEntityId,
        subjectEmail: payload.email,
        actor,
      }).unwrap();

      dispatch(
        openMessage({
          messageSeverity: 'success',
          message: linkResult?.alreadyLinked
            ? 'Subject onboarded and was already linked to this site.'
            : 'Subject onboarded and linked to this site successfully.',
        })
      );
      resetForm({ values: subjectInitialValues() });
      onSuccess?.();
      onClose();
    } catch (error) {
      const messageText = sanitizeApiErrorMessage(error) || '';
      const isStudySiteConflict =
        error?.status === 409 ||
        messageText.toLowerCase().includes('already linked to another site');

      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: isStudySiteConflict
            ? 'Subject was onboarded but is already linked to another site on this clinical study. Link them from that site instead.'
            : `${messageText || 'Subject was onboarded but linking to this site failed.'} You may need to link manually.`,
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={isBusy ? undefined : onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="span">
            Onboard Subject
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CustomButton
              variant="outlined"
              size="small"
              startIcon={<CloudUploadOutlinedIcon />}
              component="label"
              disabled={isBusy}
            >
              Upload Subjects
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept=".csv"
                disabled={isBusy}
                onChange={(e) => handleFileUpload(e.target.files)}
              />
            </CustomButton>
            <IconButton aria-label="close" onClick={onClose} size="small" disabled={isBusy}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Creates a subject entity and links them to this study site. The subject will receive an email with
          onboarding instructions to complete Stripe verification.
        </Typography>
        <Formik
          initialValues={subjectInitialValues()}
          validationSchema={entityOnboardValidationSchema}
          onSubmit={handleSubmit}
          validateOnChange
          validateOnBlur
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            isSubmitting: formSubmitting,
            submitCount,
            isValid,
          }) => (
            <Form>
              {submitCount > 0 && !isValid && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Please fix the highlighted errors before submitting.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="externalMasterId"
                    label="Subject Reference ID"
                    value={values.externalMasterId}
                    onChange={handleChange}
                    error={touched.externalMasterId && Boolean(errors.externalMasterId)}
                    helperText={
                      (touched.externalMasterId && errors.externalMasterId) ||
                      'External identifier for this subject (e.g. participant ID)'
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <PersonFields
                  prefix="individual"
                  title="Subject Details"
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  values={values}
                  setFieldValue={setFieldValue}
                  submitCount={submitCount}
                />

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography fontWeight={600}>Bank Details</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="bankDetails.accountHolderName"
                    label="Account Holder Name"
                    value={values.bankDetails.accountHolderName}
                    onChange={handleChange}
                    error={
                      touched.bankDetails?.accountHolderName &&
                      Boolean(errors.bankDetails?.accountHolderName)
                    }
                    helperText={
                      touched.bankDetails?.accountHolderName && errors.bankDetails?.accountHolderName
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled
                    name="bankDetails.accountHolderType"
                    label="Account Holder Type"
                    value="Individual"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="bankDetails.accountNumber"
                    label="Account Number"
                    value={values.bankDetails.accountNumber}
                    onChange={handleChange}
                    error={
                      touched.bankDetails?.accountNumber && Boolean(errors.bankDetails?.accountNumber)
                    }
                    helperText={touched.bankDetails?.accountNumber && errors.bankDetails?.accountNumber}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="bankDetails.confirmAccountNumber"
                    label="Confirm Account Number"
                    value={values.bankDetails.confirmAccountNumber}
                    onChange={handleChange}
                    error={
                      touched.bankDetails?.confirmAccountNumber &&
                      Boolean(errors.bankDetails?.confirmAccountNumber)
                    }
                    helperText={
                      touched.bankDetails?.confirmAccountNumber &&
                      errors.bankDetails?.confirmAccountNumber
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    name="bankDetails.routingNumber"
                    label="Routing Number"
                    value={values.bankDetails.routingNumber}
                    onChange={handleChange}
                    error={
                      touched.bankDetails?.routingNumber && Boolean(errors.bankDetails?.routingNumber)
                    }
                    helperText={touched.bankDetails?.routingNumber && errors.bankDetails?.routingNumber}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={touched.bankDetails?.country && Boolean(errors.bankDetails?.country)}
                  >
                    <InputLabel id="bankCountry-label">Bank Country</InputLabel>
                    <Select
                      labelId="bankCountry-label"
                      name="bankDetails.country"
                      value={values.bankDetails.country}
                      label="Bank Country"
                      onChange={(event) => {
                        const selected = bankCountries.find((c) => c.code === event.target.value);
                        setFieldValue('bankDetails.country', event.target.value);
                        setFieldValue('bankDetails.currency', selected?.currency || '');
                      }}
                      renderValue={(value) => {
                        const country = getCountryByCode(value, bankCountries);
                        return country ? formatCountryOption(country) : value;
                      }}
                    >
                      {bankCountries.map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                          {formatCountryOption(option)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{touched.bankDetails?.country && errors.bankDetails?.country}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    disabled
                    name="bankDetails.currency"
                    label="Currency"
                    value={values.bankDetails.currency}
                  />
                </Grid>

                <Grid item xs={12} display="flex" justifyContent="center" gap={2} pt={2} pb={1}>
                  <CustomButton variant="outlined" onClick={onClose} disabled={formSubmitting || isBusy}>
                    Cancel
                  </CustomButton>
                  <CustomButton type="submit" variant="contained" disabled={formSubmitting || isBusy}>
                    {formSubmitting || isBusy ? (
                      <CircularProgress size={24} sx={{ color: 'white' }} />
                    ) : (
                      'Onboard & Link'
                    )}
                  </CustomButton>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardSubjectDialog;
